import AddCommentIcon from "@mui/icons-material/AddComment";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import Chip from "@mui/material/Chip";
import Grow from "@mui/material/Grow";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import * as Constants from "config";
import _, { uniqBy, throttle } from "lodash";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSession } from "users/__session/session-context";
import {
  addComment,
  getCommentsByField,
  getIsLoadingComments,
  populateThreadComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";

import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import MessagingAPIService, { MessageQuery } from "messaging/_api/messaging-api-service";
import AudienceSwitch from "work/proposals/comments/_view/audience-switch";
import CommentItem from "work/proposals/comments/_view/comment-item";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";

const CommentsContainer = styled("div")(() => ({
  display: "contents",
  height: "100%",
}));
const CommentsList = styled(List)(() => ({
  overflowY: "auto",
}));
const PostCommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
  "& .MuiFormHelperText-root": {
    margin: 0
  }
}));
const BackToTopChipContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const BackToTopChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  top: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));
const PostOptionsContainer = styled("span")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: theme.spacing(1),
}));
const MarkAllAsReadButton = styled(Button)(() => ({
  marginLeft: "auto",
}));
const NoComments = styled(ListItemText)(({ theme }) => ({
  ".MuiTypography-root": {
    fontSize: '1.2rem',
    textAlign: "center",
  },
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(2)
}));

type CommentsProps = {
  className?: string;
  proposal: Proposal;
  commentThreads: CommentThread[];
  field: ProposalField;
  isSaving: boolean;
};

export default function Comments(props: Readonly<CommentsProps>) {
  const { className, proposal, commentThreads, field, isSaving } = props;

  const topRef = useRef<HTMLDivElement>(null);
  const commentsListRef = useRef<HTMLUListElement>(null);

  const [backToTopVisible, setBackToTopVisible] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");
  const [isExternal, setIsExternal] = React.useState(false);

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const fieldComments = useSelector(getCommentsByField(proposal.id, field));
  const isLoadingComments = useSelector(getIsLoadingComments(proposal.id, field));

  const handleWindowScrolled = useCallback((event: Event) => {
    const el = event.currentTarget as HTMLElement | null;
    if (!el) return;

    const denom = el.scrollHeight - el.clientHeight;
    const scrollPercent = denom > 0 ? Math.round((el.scrollTop / denom) * 100) : 0;

    setBackToTopVisible(scrollPercent > 5);
  }, []);

  const throttledScrollEventHandler = useMemo(() => {
    return throttle(handleWindowScrolled, Constants.backToTopThrottleDelay)
  }, [handleWindowScrolled]);

  const scrollToBottom = useCallback(() => {
    globalThis.setTimeout(() => {
      topRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }, 500);
  }, []);

  const subscriberUserIds = useMemo(
    () =>
      uniqBy(
        [
          ...(fieldComments?.map((comment: Comment) => comment.senderId).filter(Boolean) ?? []),
          ...commentThreads.flatMap((thread: CommentThread) => thread.subscribers.map((sub) => sub.userId)),
        ],
        (id) => id?.value
      )
        .filter(Boolean)
        .map((id) => id!.value),
    [fieldComments, commentThreads]
  );
  const subscriberUserIdsSet = useMemo(() => new Set(subscriberUserIds), [subscriberUserIds]);

  useGetIndividualsByUserIdsQuery(
    { userIds: subscriberUserIdsSet },
    { skip: subscriberUserIds.length === 0 }
  );

  useEffect(() => {
    // Attach the listener when the element exists, and clean up properly
    const el = commentsListRef.current;
    if (!el) return;

    el.addEventListener("scroll", throttledScrollEventHandler);

    return () => {
      el.removeEventListener("scroll", throttledScrollEventHandler);
      throttledScrollEventHandler.cancel(); // lodash throttle cleanup
    };
  }, [throttledScrollEventHandler]);

  useEffect(() => {
    scrollToBottom();
  }, [commentThreads, fieldComments, scrollToBottom]);

  useEffect(() => {
    commentThreads.forEach((thread) => {
      if (field.isEqualTo(thread.field)) {
        const query = new MessageQuery(
          new MessageParameters(new MessageFilterParameters({})),
          PaginationParameters.create({
            pageIndex: 0,
            pageSize: 100,
          })
        );
        dispatch(
          populateThreadComments({
            session,
            thread,
            query,
          })
        );
      }
    });
  }, [field, commentThreads, dispatch, session]);



  function handleCommentPosted() {
    const comment = draftComment(field, commentText, isExternal);
    if (!comment) {
      console.error("Failed to draft comment");
      return;
    }
    dispatch(addComment(comment));
    setCommentText("");
    setIsExternal(false);
    scrollToBottom();
  }

  function draftComment(
    field: ProposalField,
    commentText: string,
    isExternal: boolean,
    isAutoComment: boolean = false
  ): Comment | undefined {
    if (!proposal?.id || !session.user?.id) return;

    const thread = getOrCreateCommentThread(field, isExternal);
    return new Comment({
      thread,
      content: commentText,
      senderId: session.user.id,
      isAutoGenerated: isAutoComment,
    });
  }

  function getOrCreateCommentThread(field: ProposalField, isExternal: boolean): CommentThread {
    const existingThread = commentThreads?.find((thread) => {
      return (
        thread.field.isEqualTo(field) && thread.isExternal === isExternal && thread.proposalId.isEqualTo(proposal.id)
      );
    });
    if (existingThread) {
      return existingThread;
    } else {
      return CommentThread.fromProposalField({
        proposal,
        field: field ?? ProposalField.General,
        isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        isExternal,
      });
    }
  }

  async function handleMarkAllAsRead() {
    try {
      let unreadComments = fieldComments.filter(
        (comment) => !comment.isReadByUser(session.user?.id) && !comment.markedForReadReceipt
      );
      if (unreadComments.length < 1) {
        console.warn("No unread comments to mark as read.");
        return;
      }
      unreadComments = unreadComments.map((comment) => {
        return comment.markForReadReceipt();
      });
      const unreadMessages = unreadComments.map((comment) =>
        comment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        })
      );
      const apiService = new MessagingAPIService(session);
      const returnedMessages = await apiService.createBulkMessages(unreadMessages);
      if (!returnedMessages) {
        console.error("Failed to mark all comments as read.");
        return;
      }
      returnedMessages.forEach((message) => {
        dispatch(addComment(Comment.fromMessage(message)));
      });
    } catch (error) {
      console.error("Error marking all comments as read:", error);
    }
  }

  const sortedComments = _(fieldComments)
    .uniqBy(c => c.id?.value)
    .orderBy(c => c.published?.valueOf() ?? 0, 'desc')
    .value();

  return isLoadingComments ? (
    <Loader />
  ) : (
    <CommentsContainer className={className}>
      <PostCommentInput
        placeholder="Type your comment here..."
        multiline
        rows={4}
        variant="outlined"
        fullWidth
        value={commentText}
        helperText={
          <PostOptionsContainer>
            <AudienceSwitch
              proposal={proposal}
              isExternal={isExternal}
              onIsExternalChanged={(isExternal) => setIsExternal(isExternal)}
            />
            <MarkAllAsReadButton
              color="primary"
              disabled={
                !sortedComments.some(
                  (comment) => !comment.isReadByUser(session.user?.id) && !comment.markedForReadReceipt
                )
              }
              startIcon={<MarkEmailRead />}
              onClick={handleMarkAllAsRead}
            >
              Mark all read
            </MarkAllAsReadButton>
          </PostOptionsContainer>
        }
        slotProps={{
          input: {
            endAdornment: (
              <LoadingButton
                color="primary"
                endIcon={<AddCommentIcon />}
                disabled={commentText.trim() === ""}
                loading={isSaving}
                onClick={handleCommentPosted}
              >
                Post
              </LoadingButton>
            ),
          },
        }}
        onKeyDown={(event) => {
          if (event.key === KeyboardKey.Enter && commentText.trim() !== "") {
            event.preventDefault();
            handleCommentPosted();
          }
        }}
        onChange={(event) => setCommentText(event.target.value)}
      />
      {Boolean(fieldComments?.length && fieldComments.length > 0) && (
        <BackToTopChipContainer>
          <Grow in={backToTopVisible}>
            <BackToTopChip
              label="Jump to latest"
              icon={<ArrowUpwardIcon style={{ color: "white" }} />}
              onClick={() =>
                topRef?.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "nearest",
                })
              }
            />
          </Grow>
        </BackToTopChipContainer>
      )}
      <CommentsList ref={commentsListRef}>
        <div ref={topRef} />
        {sortedComments?.length === 0 && (
          <ListItem key={Guid.generate().value} disableGutters>
            <NoComments primary="No comments yet" />
          </ListItem>
        )}
        {sortedComments.length > 0 &&
          sortedComments?.map((comment) => (
            <CommentItem
              key={comment.id?.value}
              comment={comment}
              audienceSwitch={
                <AudienceSwitch
                  proposal={proposal}
                  disabled={comment.id && comment.thread.isExternal}
                  isExternal={comment.thread.isExternal}
                  onIsExternalChanged={(isExternal) => {
                    if (!comment.thread.isExternal && isExternal) {
                      comment = comment.markForCreation();
                      comment.thread.id = undefined;
                    }
                    comment.thread.isExternal = isExternal;
                  }}
                />
              }
            />
          ))}
      </CommentsList>
    </CommentsContainer>
  );
}
